/**
 * Shop the Look — the lifestyle media CTA and the panel it fills.
 *
 * There is no drawer of its own: the trigger opens the quickview modal in a
 * second mode, where the panel holds the look's product cards and a card's own
 * quickview opens beside them (desktop) or under them (mobile). Adding to cart
 * from either closes the whole drawer, because it is still one modal.
 */

import "../global/product-card/product-card-impressions";

const MODAL_ID = "quickview-modal";
const CARD_SECTION_ID = "shop-the-look-card";
// Matches the half-width the cards fall back to in the stylesheet.
const SLIDES_PER_VIEW = 2;
// Kept in step with the `tablet-wide` breakpoint the panel styles switch on.
const MOBILE_QUERY = "(max-width: 1023px)";

// Cards are re-parsed on every open — nodes can't be re-used — so what is held
// between opens is the response text.
const cardMarkup = new Map();

const fetchCard = (handle) => {
  if (!cardMarkup.has(handle)) {
    const url = new URL(window.productUrl(handle), window.location.origin);
    url.searchParams.set("section_id", CARD_SECTION_ID);

    cardMarkup.set(
      handle,
      fetch(url)
        .then((response) => response.text())
        .catch(() => {
          cardMarkup.delete(handle);
          return null;
        }),
    );
  }

  return cardMarkup.get(handle);
};

const parseCard = (markup) => {
  if (!markup) return null;

  return new DOMParser()
    .parseFromString(markup, "text/html")
    .querySelector("product-card");
};

const wrapCard = (content) => {
  const card = document.createElement("div");
  card.className = "shop-the-look__card";
  card.dataset.swiperClass = "swiper-slide";
  card.append(content);

  return card;
};

const skeletonCard = () => {
  const skeleton = document.createElement("div");
  skeleton.className = "shop-the-look__skeleton";

  return wrapCard(skeleton);
};

class ShopTheLookTrigger extends HTMLElement {
  connectedCallback() {
    this.button = this.closest("button");

    if (!this.button) return;

    this.button.addEventListener("mouseenter", () => this._load());

    this.button.addEventListener("click", () => {
      this.button.dispatchEvent(
        new CustomEvent("markup-modal:open", {
          bubbles: true,
          detail: {
            target: MODAL_ID,
            // Whatever quickview was last in the modal is not part of this look.
            html: "",
            shopTheLook: { count: this.handles.length, cards: this._load() },
          },
        }),
      );
    });
  }

  _load() {
    return Promise.all(this.handles.map(fetchCard));
  }

  get handles() {
    return (this.getAttribute("products") || "").split(",").filter(Boolean);
  }
}

class ShopTheLookPanel extends HTMLElement {
  connectedCallback() {
    this.modal = this.closest("markup-modal");
    this.cards = this.querySelector("[data-cards]");

    if (!this.modal || !this.cards) return;

    this.mobile = window.matchMedia(MOBILE_QUERY);

    this.handleOpen = (e) => {
      if (e.detail.target !== this.modal.id) return;

      if (e.detail.shopTheLook) {
        this._showLook(e.detail.shopTheLook);
      } else if (this.modal.hasAttribute("shop-the-look")) {
        this._showQuickview(e.detail.html);
      }
    };

    this.handleClosed = () => this._reset();
    this.handleBreakpoint = () => this._syncCarousel();

    document.addEventListener("markup-modal:open", this.handleOpen);
    document.addEventListener("markup-modal:closed", this.handleClosed);
    this.mobile.addEventListener("change", this.handleBreakpoint);
  }

  disconnectedCallback() {
    document.removeEventListener("markup-modal:open", this.handleOpen);
    document.removeEventListener("markup-modal:closed", this.handleClosed);
    this.mobile?.removeEventListener("change", this.handleBreakpoint);

    this._destroyCarousel();
  }

  _showLook({ count, cards }) {
    this.modal.setAttribute("shop-the-look", "");
    this.modal.removeAttribute("quickview-open");

    const request = {};
    this.request = request;

    this._setCards(Array.from({ length: count }, skeletonCard));

    cards.then((markup) => {
      if (this.request !== request) return;

      this._setCards(markup.map(parseCard).filter(Boolean).map(wrapCard));
    });
  }

  /**
   * A card's quickview replaces whatever the modal was showing, so the panel
   * only has to reveal it.
   */
  _showQuickview(html) {
    this.modal.setAttribute("quickview-open", "");

    if (!this.mobile.matches) return;

    // On mobile the quickview lands under the cards, off screen — and it has no
    // height to scroll to until its markup has arrived.
    Promise.resolve(html).then(() => {
      window.requestAnimationFrame(() => {
        this.modal
          .querySelector("[data-quickview-panel]")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }

  _reset() {
    this.request = null;
    this._destroyCarousel();
    this.cards.replaceChildren();
    this.modal.removeAttribute("shop-the-look");
    this.modal.removeAttribute("quickview-open");
    this.scrollTop = 0;
  }

  _setCards(cards) {
    this._destroyCarousel();
    this.cards.replaceChildren(...cards);
    this._syncCarousel();
  }

  /** The cards are a carousel on mobile and a two-up grid above it. */
  _syncCarousel() {
    // Two cards already fit the mobile row, so there is nothing to page through.
    if (this.mobile.matches && this.cards.children.length > SLIDES_PER_VIEW) {
      this._initCarousel();
    } else {
      this._destroyCarousel();
    }
  }

  _initCarousel() {
    if (this.swiper || this.loadingSwiper) return;

    this.loadingSwiper = window.loadSwiper.then(({ Swiper, Pagination }) => {
      this.loadingSwiper = null;

      // The cards can have been swapped or cleared while the library loaded.
      if (this.swiper || !this.mobile.matches) return;
      if (this.cards.children.length <= SLIDES_PER_VIEW) return;

      this._toggleSwiperClasses(true);

      this.swiper = new Swiper(this.querySelector("[data-viewport]"), {
        modules: [Pagination],
        slidesPerView: SLIDES_PER_VIEW,
        speed: 400,
        pagination: {
          el: this.querySelector("[data-pagination]"),
          clickable: true,
        },
      });
    });
  }

  _destroyCarousel() {
    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
    this._toggleSwiperClasses(false);
  }

  /**
   * Swiper's own classes are applied at runtime so the cards still lay out as a
   * row before the library has loaded, and as a grid once it is torn down.
   */
  _toggleSwiperClasses(add) {
    this.querySelectorAll("[data-swiper-class]").forEach((el) => {
      el.classList.toggle(el.dataset.swiperClass, add);
    });
  }
}

if (!customElements.get("shop-the-look-trigger")) {
  customElements.define("shop-the-look-trigger", ShopTheLookTrigger);
}

if (!customElements.get("shop-the-look-panel")) {
  customElements.define("shop-the-look-panel", ShopTheLookPanel);
}
